import { useCallback, useState } from "react";

import {
  EmailContentFlow,
  EmailContentType,
  EmailEntryPoint,
  EmailDeliveryStatus,
  Nullable,
  OrganizationRoutes,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import { enqueueSnackbar } from "notistack";

import { AmplitudeEvents } from "../../../common/constants/amplitude";
import { useEmailEditorContext } from "../../../common/contexts/EmailEditorContext";
import { useAmplitude } from "../../../common/hooks/amplitude/useAmplitude";
import {
  CREATION_FLOW_QUERY_PARAM,
  SEND_TO_MORE_RECIPIENTS_QUERY_PARAM,
} from "../FullScreenEmailEditorContainer/contexts/FullScreenEmailEditorContext";
import { hasCampaignContext } from "../FullScreenEmailEditorContainer/helpers";
import {
  getSystemTemplate,
  WaypointTemplateKey,
} from "../FullScreenEmailEditorContainer/FullScreenEmailEditorTemplate/useWaypointTemplate";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { buildUrlWithQuery } from "@/common/helpers/router";
import { defaultQueryRetry, trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { EmailAuctionRecipientAudience, EmailContactSelection } from "@/common/types/trpc";

interface BaseEmailEditorDefaultValues {
  subject?: string;
  body?: string;
  senderName?: string;
  recipientEmails?: string[];
  flow: EmailContentFlow;
  entryPoint: EmailEntryPoint;
  contactListIds?: string[];
  contactSelection?: EmailContactSelection;
}

type SpecificEmailEditorDefaultValues = {
  emailType: Nullable<EmailContentType>;
  formId?: string;
  thankYouTransactionId?: string;
  waypointTemplateKey?: string;
  ticketingOccurrenceId?: string;
  auctionRecipientAudience?: EmailAuctionRecipientAudience;
};

export type OpenEmailEditorDefaultValues = BaseEmailEditorDefaultValues & SpecificEmailEditorDefaultValues;

type OpenEmailEditorProps = (
  | { emailContentId: string; waypointTemplateKey?: string; sendToMoreRecipients?: boolean }
  | OpenEmailEditorDefaultValues
) & {
  queryParamsToExclude?: string[];
  onExitUrl?: string;
  isCreationFlow?: boolean;
};

export const buildNewEmailClickedParams = ({
  entryPoint,
  emailType,
  flow,
  waypointTemplateKey,
}: {
  entryPoint: EmailEntryPoint;
  emailType: Nullable<EmailContentType>;
  flow: EmailContentFlow;
  waypointTemplateKey?: string;
}) => ({
  from: entryPoint,
  pathname: entryPoint,
  emailType,
  category: emailType,
  flow,
  templateKey: waypointTemplateKey ?? null,
});

export const useOpenEmailEditor = () => {
  const { t } = useTranslate();
  const searchParams = useSearchParams();
  const query = Object.fromEntries((searchParams ?? []).entries());
  const { logAmplitudeEvent } = useAmplitude();
  const router = useRouter();
  const { organization } = useCurrentUserContext();
  const { openEmailEditorDrawer } = useEmailEditorContext();
  const { mutateAsync: createEmailContent } = trpc.createEmailContent.useMutation();
  const utils = trpc.useUtils();
  const [isLoading, setIsLoading] = useState(false);

  const handleCreateEmailContent = useCallback(
    async (defaultValues: OpenEmailEditorDefaultValues) => {
      const { flow, entryPoint, emailType } = defaultValues;
      logAmplitudeEvent(
        AmplitudeEvents.NewEmailClicked,
        buildNewEmailClickedParams({
          entryPoint,
          emailType,
          flow,
          waypointTemplateKey: defaultValues.waypointTemplateKey,
        })
      );

      const formId = "formId" in defaultValues ? defaultValues.formId : undefined;

      const { data } = await createEmailContent({
        subject: defaultValues?.subject || "",
        body: defaultValues?.body || "",
        senderName: defaultValues?.senderName || organization?.name || "",
        recipientEmails: defaultValues?.contactSelection ? undefined : defaultValues?.recipientEmails,
        entryPoint: defaultValues.entryPoint,
        flow,
        emailType: defaultValues?.emailType,
        formId,
        contactListIds: defaultValues?.contactListIds,
        contactSelection: defaultValues?.contactSelection,
        ticketingOccurrenceId: defaultValues?.ticketingOccurrenceId ?? undefined,
        auctionRecipientAudience: defaultValues?.auctionRecipientAudience ?? undefined,
        waypointTemplateKey: defaultValues?.waypointTemplateKey ?? undefined,
        systemTemplate: defaultValues.waypointTemplateKey
          ? getSystemTemplate(defaultValues.waypointTemplateKey as WaypointTemplateKey, hasCampaignContext(formId))
          : undefined,
      });
      void utils.getCommunicationEmailStatus.invalidate(undefined, { refetchType: "none" });
      return data;
    },
    [logAmplitudeEvent, createEmailContent, organization?.name, utils.getCommunicationEmailStatus]
  );

  const openEmailEditor = useCallback(
    async (props: OpenEmailEditorProps): Promise<boolean> => {
      const hasEmailContentId = "emailContentId" in props;
      const isCreationFlow = !hasEmailContentId || Boolean(props.isCreationFlow);
      setIsLoading(true);

      try {
        let emailContent: { id: string; flow: EmailContentFlow } | undefined;

        if (hasEmailContentId) {
          const result = await utils.getEmailContent
            .fetch(
              { emailContentId: props.emailContentId },
              {
                retry: (failureCount, error) =>
                  error.data?.code !== "email_content_not_found" && defaultQueryRetry(failureCount, error),
              }
            )
            .catch(() => undefined);
          if (!result?.emailContent) {
            enqueueSnackbar(t("dashboard", "EmailEditor.error.failToGetEmail"), { vibe: "danger" });
            void utils.getCommunicationEmailList.invalidate();
            return false;
          } else {
            const delivery = await utils.getEmailDeliveryFromContent.fetch({
              emailContentId: props.emailContentId,
            });

            if (delivery && delivery?.status === EmailDeliveryStatus.Processing) {
              enqueueSnackbar(t("dashboard", "EmailEditor.error.emailDeliveryInProgress"), { vibe: "warning" });
              return false;
            }
            emailContent = { id: result.emailContent.id, flow: result.emailContent.flow };
          }
        } else {
          const result = await handleCreateEmailContent(props);
          if (!result) {
            enqueueSnackbar(t("dashboard", "EmailEditor.error.failToCreateEmail"), { vibe: "danger" });
            return false;
          } else {
            emailContent = { id: result.id, flow: result.flow };
          }
        }

        if (emailContent.flow === EmailContentFlow.Builder) {
          const queryParamsToExclude = props.queryParamsToExclude || [];
          const restOfQueryParams = Object.fromEntries(
            Object.entries(query).filter(([key]) => !queryParamsToExclude.includes(key))
          );
          if (queryParamsToExclude.length > 0) {
            // Make sure to remove the query param from the history to avoid an infinite loop
            await router.replace({
              pathname: router.pathname,
              query: restOfQueryParams,
            });
          }
          // make sure to remove the query params in the onExitUrl to avoid an infinite loop
          const onExitUrl = props.onExitUrl || buildUrlWithQuery(router.asPath, restOfQueryParams);
          const sendToMoreRecipients = "sendToMoreRecipients" in props && Boolean(props.sendToMoreRecipients);
          await router.push({
            pathname: OrganizationRoutes.FullScreenEmailEditor,
            ...(sendToMoreRecipients ? { hash: "recipients" } : {}),
            query: {
              ...(sendToMoreRecipients ? { [SEND_TO_MORE_RECIPIENTS_QUERY_PARAM]: true } : {}),
              ...restOfQueryParams,
              emailContentId: emailContent.id,
              onExitUrl,
              ...(isCreationFlow ? { [CREATION_FLOW_QUERY_PARAM]: true } : {}),
              ...("waypointTemplateKey" in props && props.waypointTemplateKey
                ? { waypointTemplateKey: props.waypointTemplateKey }
                : {}),
              ...(!hasEmailContentId && "thankYouTransactionId" in props && props.thankYouTransactionId
                ? { thankYouTransactionId: props.thankYouTransactionId }
                : {}),
            },
          });
        } else {
          openEmailEditorDrawer(
            emailContent.id,
            "thankYouTransactionId" in props ? props.thankYouTransactionId : undefined
          );
        }
        return true;
      } finally {
        setIsLoading(false);
      }
    },
    [
      handleCreateEmailContent,
      openEmailEditorDrawer,
      query,
      router,
      t,
      utils.getEmailContent,
      utils.getCommunicationEmailList,
      utils.getEmailDeliveryFromContent,
    ]
  );

  const createEmailContentWithoutOpening = useCallback(
    async (defaultValues: OpenEmailEditorDefaultValues) => {
      setIsLoading(true);
      try {
        return await handleCreateEmailContent(defaultValues);
      } finally {
        setIsLoading(false);
      }
    },
    [handleCreateEmailContent]
  );

  return { openEmailEditor, createEmailContentWithoutOpening, isLoading };
};
